import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { integrationsApi } from '../api/integrations';
import { Integration, CreateIntegrationRequest } from '../types';

export function useIntegrations() {
  const { addNotification } = useApp();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // tenant_id is automatically extracted from JWT token on the backend
      const response = await integrationsApi.list();
      
      if (response.success && response.data) {
        setIntegrations(response.data.integrations);
      } else {
        setError(response.error || 'Failed to fetch integrations');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch integrations';
      setError(errorMessage);
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const createIntegration = async (data: CreateIntegrationRequest) => {
    try {
      setLoading(true);
      
      const response = await integrationsApi.create(data);
      
      if (response.success && response.data) {
        setIntegrations(prev => [...prev, response.data!]);
        addNotification({
          type: 'success',
          title: 'Integration Created',
          message: `${data.type} integration created successfully`,
        });
        return response.data;
      } else {
        addNotification({
          type: 'error',
          title: 'Failed to Create Integration',
          message: response.error || 'Please try again',
        });
        return null;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to create integration';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateIntegration = async (id: string, data: Partial<Integration>) => {
    try {
      setLoading(true);
      
      const response = await integrationsApi.update(id, data);
      
      if (response.success && response.data) {
        setIntegrations(prev => 
          prev.map(integration => 
            integration.id === id ? response.data! : integration
          )
        );
        addNotification({
          type: 'success',
          title: 'Integration Updated',
          message: 'Integration updated successfully',
        });
        return response.data;
      } else {
        addNotification({
          type: 'error',
          title: 'Failed to Update Integration',
          message: response.error || 'Please try again',
        });
        return null;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update integration';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteIntegration = async (id: string) => {
    try {
      setLoading(true);
      
      const response = await integrationsApi.delete(id);
      
      if (response.success) {
        setIntegrations(prev => prev.filter(integration => integration.id !== id));
        addNotification({
          type: 'success',
          title: 'Integration Deleted',
          message: 'Integration deleted successfully',
        });
        return true;
      } else {
        addNotification({
          type: 'error',
          title: 'Failed to Delete Integration',
          message: response.error || 'Please try again',
        });
        return false;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to delete integration';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const connectWhatsApp = async (id: string, attemptConnection: boolean = false) => {
    try {
      setLoading(true);
      
      const response = await integrationsApi.connectWhatsApp(id, attemptConnection);
      
      if (response.success && response.data) {
        // Refresh integrations to get updated status
        await fetchIntegrations();
        
        // Only show success notification if actually connected (not just QR code generated)
        if (response.data.status === 'active') {
          addNotification({
            type: 'success',
            title: 'WhatsApp Connected',
            message: `Connected to ${response.data.phone_number || 'WhatsApp'}`,
          });
        }
        // For 'pending' status (QR code generated), don't show notification
        // The UI will handle displaying the QR code
        
        return response.data;
      } else {
        addNotification({
          type: 'error',
          title: 'Connection Failed',
          message: response.error || 'Failed to connect to WhatsApp',
        });
        return null;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to connect WhatsApp';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetch integrations on mount
  useEffect(() => {
    fetchIntegrations();
  }, []);

  return {
    integrations,
    loading,
    error,
    fetchIntegrations,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    connectWhatsApp,
  };
}
